import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  Typography,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import NotesList from "./NotesList";
import AddNoteForm from "./NotesForm";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Note } from "../../../../../../../../../api/homeSiteNotes/types";
import {
  useCreateNewNote,
  useUpdateNotes,
} from "../../../../../../../../../reactQuery/homeSites/notes/homeSiteNotes";
import { GridExpandMoreIcon } from "@mui/x-data-grid";
import { DivisionMileStone } from "../../../../../../../../../api/divisionMileStones/types";
import { ALL_MILESTONES, GeneralComments, STANDARD_HEIGHT_OF_DROPDOWN } from "../../../../../../../../../utils/constants";
import { thinDarkScrollbar } from "../../../../../../../../../utils/scrollbarStyles";

interface NotesComponentProps {
  userId: string;
  notesState: Note[];
  homeSiteId: string;
  mileStones: DivisionMileStone[];
  divisionCode: string;
  conciergeEmail: string;
  opportunityId: string;
  noteText: string;
  setNoteText: (text: string) => void;
  selectedMilestone: string;
  setSelectedMilestone: (milestone: string) => void;
  isDirty?: (dirty: boolean) => void;
  setChildHandlers?: (handlers: {
    save: () => Promise<void>;
    cancel: () => void;
  }) => void;
  showSnackbar: (
    message: string,
    severity: "success" | "error" | "info"
  ) => void;
}

const NotesComponent: React.FC<NotesComponentProps> = ({
  notesState,
  homeSiteId,
  userId,
  mileStones,
  divisionCode,
  conciergeEmail,
  opportunityId,
  noteText,
  setNoteText,
  selectedMilestone,
  setSelectedMilestone,
  isDirty,
  setChildHandlers,
  showSnackbar,
}) => {
  const { mutateAsync: createNote } = useCreateNewNote(
    divisionCode,
    homeSiteId
  );
  const { mutate: updateNote } = useUpdateNotes(divisionCode, homeSiteId);

  // Helper function to get valid milestone value for this component
  const getValidMilestoneValue = (milestone: string): string => {
    if (!milestone) {
      return "All Milestones"; // Default to All Milestones if no milestone provided
    }

    // Handle different formats of "all milestones" 
    if (milestone === "allMilestones" || milestone === ALL_MILESTONES) {
      return "All Milestones";
    }

    // Check if it's already "All Milestones"
    if (milestone === "All Milestones") {
      return milestone;
    }

    // Check if it's the GeneralComments option
    if (milestone === GeneralComments) {
      return milestone;
    }

    // Check if it's one of the available milestone names
    if (mileStones && mileStones.some(ms => ms.mileStoneName === milestone)) {
      return milestone;
    }

    // If not valid, return "All Milestones" as default
    return "All Milestones";
  };

  // Ensure selectedMilestone is always valid
  const validSelectedMilestone = getValidMilestoneValue(selectedMilestone);

  // Update the parent if the current selected milestone is invalid
  useEffect(() => {
    if (selectedMilestone !== validSelectedMilestone) {
      setSelectedMilestone(validSelectedMilestone);
    }
  }, [selectedMilestone, validSelectedMilestone, setSelectedMilestone]);

  const [showResolved, setShowResolved] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const prevMilestoneRef = useRef<string | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [resolvedPage, setResolvedPage] = useState(1);

  const ITEMS_PER_PAGE = 5;
  const toggleResolvedVisibility = () => setShowResolved((prev) => !prev);

  const handleMilestoneChange = (event: SelectChangeEvent<string>) => {
    setSelectedMilestone(event.target.value as string);
    setActivePage(1);
    setResolvedPage(1);
  };

  const handleAddClick = () => {
   // Snapshot current milestone before showing the add form
    prevMilestoneRef.current = selectedMilestone;
    setShowAddForm(true);
    setNoteText("");
    if (selectedMilestone === "All Milestones") {
      setSelectedMilestone(mileStones[0]?.mileStoneName);
    }
  };

  const handleSaveNote = useCallback(() => {
    prevMilestoneRef.current = null;
    createNote({
      description: noteText,
      divisionCode: divisionCode,
      conciergeEmail: conciergeEmail,
      homeSiteId,
      mileStoneName: selectedMilestone,
      noteType: "homeSiteNotes",
      createdBy: userId,
      opportunityId,
    });
    setShowAddForm(false);
    setNoteText("");
    showSnackbar("Note added successfully!", "success");
    isDirty && isDirty(false);
  }, [
    createNote,
    noteText,
    divisionCode,
    conciergeEmail,
    homeSiteId,
    selectedMilestone,
    userId,
    opportunityId,
    setShowAddForm,
    setNoteText,
    isDirty,
    showSnackbar,
  ]);


  // When the Add form closes without a save, restore the original milestone
  useEffect(() => {
    if (!showAddForm && prevMilestoneRef.current) {
      setSelectedMilestone(prevMilestoneRef.current);
      prevMilestoneRef.current = null;
    }
  }, [showAddForm, setSelectedMilestone]);

  const milestoneCounts = notesState
    ?.filter(
      (note) =>
        note.noteType === "homeSiteNotes" && note.status === !showResolved
    )
    .reduce((acc, note) => {
      acc[note.mileStoneName] = (acc[note.mileStoneName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

  const activeNotes = notesState?.filter(
    (note) =>
      (validSelectedMilestone === "All Milestones" ||
        note.mileStoneName === validSelectedMilestone) &&
      note.status &&
      note.noteType === "homeSiteNotes"
  );

  const resolvedNotes = notesState
    ?.filter(
      (note) =>
        (validSelectedMilestone === "All Milestones" ||
          note.mileStoneName === validSelectedMilestone) &&
        !note.status &&
        note.noteType === "homeSiteNotes"
    )
    .sort(
      (a, b) =>
        new Date(b.createdOnDate).getTime() - new Date(a.createdOnDate).getTime()
    );

  useEffect(() => {
    setChildHandlers?.({
      save: async () => handleSaveNote(),
      cancel: () => {
        setNoteText("");
        setSelectedMilestone("All Milestones");
        isDirty?.(false);
      },
    });
  }, [
    handleSaveNote,
    setNoteText,
    setSelectedMilestone,
    setChildHandlers,
    isDirty,
  ]);

  return (
    <>
      <Box
        padding={2}
        sx={{
          backgroundColor: "#FFFFFF",
          width: "504px",
          height: "480px",
          borderRadius: "4px",
          display: "flex",
          flexDirection: "column",
          padding: 2, // Add padding for spacing
          boxSizing: "border-box",
        }}
      >
        {!showAddForm ? (
          <>
            {showResolved && (
              <Box display="flex" alignItems="center" marginBottom={1}>
                <IconButton edge="start" onClick={() => setShowResolved(false)}>
                  <ArrowBackIcon />
                </IconButton>
                <Typography
                  variant="h6"
                  sx={{
                    color: "#1C2B36",
                    fontWeight: "bold",
                    fontSize: "16px",
                  }}
                >
                  Resolved Notes
                </Typography>
              </Box>
            )}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              marginBottom={1}
            >
              <FormControl
                variant="standard"
                style={{ flexGrow: 1, marginRight: "16px" }}
              >
                <Select
                  IconComponent={(props) => (
                    <GridExpandMoreIcon
                      {...props}
                      sx={{
                        fontSize: 25,
                      }}
                    />
                  )}
                  labelId="milestone-select-label"
                  value={validSelectedMilestone}
                  onChange={handleMilestoneChange}
                  label="Milestone"
                  sx={{
                    fontWeight: "400",
                    "& .MuiSelect-icon": {
                      color: "#3E5463",
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: {
                        maxHeight: STANDARD_HEIGHT_OF_DROPDOWN,
                        overflowY: "auto",
                        ...thinDarkScrollbar,
                      },
                    },
                  }}
                >
                  <MenuItem value="All Milestones">
                    All Milestones (
                    <strong>
                      {
                        notesState?.filter(
                          (nt) =>
                            nt.status === !showResolved &&
                            nt.noteType === "homeSiteNotes"
                        ).length
                      }
                    </strong>
                    )
                  </MenuItem>
                  <MenuItem key="general-comments" value={GeneralComments}>
                    {GeneralComments} (
                    <strong>
                      {
                        notesState?.filter(
                          (nt) =>
                            nt.status === !showResolved &&
                            nt.noteType === "homeSiteNotes" &&
                            nt.mileStoneName.toLowerCase() ===
                            GeneralComments.toLowerCase()
                        ).length
                      }
                    </strong>
                    )
                  </MenuItem>

                  {mileStones.map((milestone) => (
                    <MenuItem
                      key={milestone.mileStoneName}
                      value={milestone.mileStoneName}
                    >
                      {milestone.mileStoneName} (
                      <strong>
                        {milestoneCounts[milestone.mileStoneName] || 0}
                      </strong>
                      )
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {!showResolved && (
                <Button
                  variant="contained"
                  onClick={handleAddClick}
                  sx={{
                    textTransform: "none",
                  }}
                >
                  + Add Note
                </Button>
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              {!showResolved && (
                <NotesList
                  notes={activeNotes}
                  page={activePage}
                  itemsPerPage={ITEMS_PER_PAGE}
                  updateNote={updateNote}
                  setActivePage={setActivePage}
                  homeSiteId={homeSiteId}
                  userId={userId}
                  divisionCode={divisionCode}
                  title="Active Notes"
                  opportunityId={opportunityId}
                  isDirty={isDirty}
                  showSnackbar={showSnackbar}
                />
              )}

              {showResolved && (
                <NotesList
                  notes={resolvedNotes}
                  page={resolvedPage}
                  itemsPerPage={ITEMS_PER_PAGE}
                  updateNote={updateNote}
                  setActivePage={setResolvedPage}
                  homeSiteId={homeSiteId}
                  userId={userId}
                  divisionCode={divisionCode}
                  title="Resolved Notes"
                  opportunityId={opportunityId}
                  isDirty={isDirty}
                  showSnackbar={showSnackbar}
                />
              )}
            </Box>
          </>
        ) : (
          <AddNoteForm
            milestones={mileStones?.map((milestone) => milestone.mileStoneName)}
            selectedMilestone={selectedMilestone}
            setSelectedMilestone={setSelectedMilestone}
            noteText={noteText}
            setNoteText={setNoteText}
            handleSaveNote={handleSaveNote}
            handleBack={() => setShowAddForm(false)}
            onNoteDirtyChange={(dirty) => isDirty?.(dirty)}
            setChildHandlers={setChildHandlers}
          />
        )}
      </Box>
      <Box style={{ backgroundColor: "white", borderRadius: "4px", height: "34px" }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          onClick={toggleResolvedVisibility}
          sx={{
            cursor: "pointer",
            width: "130px",
            backgroundColor: "transparent",
            marginLeft: "200px"
          }}
        >
          {!showAddForm && (
            <>
              {showResolved
                ? activeNotes.length > 0 && (
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#005DAA",
                      fontWeight: "700",
                      fontSize: "12px",
                    }}
                  >
                    Show Active ({activeNotes.length})
                  </Typography>
                )
                : resolvedNotes.length > 0 && (
                  <Typography
                    variant="h4"
                    sx={{
                      color: "#005DAA",
                      fontWeight: "700",
                      fontSize: "12px",
                    }}
                  >
                    Show Resolved ({resolvedNotes.length})
                  </Typography>
                )}
            </>
          )}
        </Box>
      </Box>
    </>
  );
};

export default NotesComponent;